import { useState, useEffect, useRef } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import TourBanner from '../components/layout/TourBanner'
import { exportarDatos, importarDatos } from '../db/backup'
import { useConfig } from '../context/ConfigContext'
import { useAuth } from '../context/AuthContext'
import { obtenerUsuarios, crearUsuario, actualizarUsuario, cambiarPin } from '../db/usuarios'
import { useLicencia } from '../context/LicenciaContext'
import { useSync } from '../context/SyncContext'
import { useBTPrinter } from '../context/BTPrinterContext'

const TARJETAS_CONFIG = [
  {
    key: 'general',
    titulo: 'General',
    subtitulo: 'Nombre de la tienda y módulo de fiado',
    colorBg: 'bg-tienda-accent/15',
    colorIcon: 'text-tienda-accent',
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
  },
  {
    key: 'usuarios',
    titulo: 'Usuarios',
    subtitulo: 'Gestiona accesos y PINs del equipo',
    colorBg: 'bg-blue-500/15',
    colorIcon: 'text-blue-400',
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    key: 'respaldo',
    titulo: 'Respaldo',
    subtitulo: 'Exporta e importa todos los datos',
    colorBg: 'bg-tienda-success/15',
    colorIcon: 'text-tienda-success',
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    ),
  },
  {
    key: 'sync',
    titulo: 'Licencia y Sync',
    subtitulo: 'Activa tu plan y sincroniza con la nube',
    colorBg: 'bg-purple-500/15',
    colorIcon: 'text-purple-400',
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="23 4 23 10 17 10" />
        <polyline points="1 20 1 14 7 14" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
      </svg>
    ),
  },
]

function Header({ titulo, onVolver }) {
  return (
    <div className="flex items-center gap-3 p-4 border-b border-tienda-border bg-tienda-surface flex-shrink-0">
      <button
        onClick={onVolver}
        className="w-9 h-9 flex items-center justify-center text-tienda-muted active:scale-95 transition-transform"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M19 12H5M5 12l7-7M5 12l7 7" />
        </svg>
      </button>
      <h2 className="text-lg font-bold text-tienda-text">{titulo}</h2>
    </div>
  )
}

export default function Configuracion() {
  const [modo, setModo] = useState('inicio')
  const { needRefresh: [needRefresh], updateServiceWorker } = useRegisterSW()

  if (modo !== 'inicio') {
    const tarjeta = TARJETAS_CONFIG.find(t => t.key === modo)
    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header titulo={tarjeta.titulo} onVolver={() => setModo('inicio')} />
        <div className="flex-1 overflow-y-auto p-4">
          {modo === 'general'   && <TabGeneral />}
          {modo === 'usuarios'  && <TabUsuarios />}
          {modo === 'respaldo'  && <TabRespaldo />}
          {modo === 'sync'      && <TabSync />}
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header inicio */}
      <div className="px-4 pt-5 pb-4 border-b border-tienda-border bg-tienda-surface flex-shrink-0">
        <h1 className="text-2xl font-bold text-tienda-text">Configuración</h1>
        <p className="text-tienda-muted text-sm">Administra tu tienda y cuenta</p>
      </div>

      {/* Tour banner — solo primera visita */}
      <TourBanner
        moduleKey="configuracion"
        titulo="Ajusta tu tienda a tu medida"
        descripcion="Cambia el nombre de tu tienda, agrega usuarios con PIN, haz respaldos de seguridad y activa tu licencia o la sincronización en la nube."
      />

      {/* Tarjetas */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">

        {/* Tarjeta de actualización — solo visible cuando hay versión pendiente */}
        {needRefresh && (
          <TarjetaActualizacion onAplicar={() => updateServiceWorker(true)} />
        )}

        {TARJETAS_CONFIG.map(t => (
          <button
            key={t.key}
            onClick={() => setModo(t.key)}
            className="w-full card p-5 flex items-center gap-4 active:scale-[0.98] transition-transform text-left"
          >
            <div className={`w-14 h-14 rounded-2xl ${t.colorBg} ${t.colorIcon} flex items-center justify-center flex-shrink-0`}>
              {t.icon}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-tienda-text text-lg leading-tight">{t.titulo}</p>
              <p className="text-tienda-muted text-sm mt-0.5 leading-snug">{t.subtitulo}</p>
            </div>
            <svg className="w-5 h-5 text-tienda-muted flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
        ))}
      </div>
    </div>
  )
}

function TarjetaActualizacion({ onAplicar }) {
  const [aplicando, setAplicando] = useState(false)

  async function handleAplicar() {
    setAplicando(true)
    onAplicar()
    // updateServiceWorker recarga la página, el estado no importa después
  }

  return (
    <div className="card p-5 border-tienda-accent/40 bg-tienda-accent/5">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-2xl bg-tienda-accent/15 text-tienda-accent flex items-center justify-center flex-shrink-0">
          <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 2v6h-6" />
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
            <path d="M3 22v-6h6" />
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-tienda-text text-lg leading-tight">Actualización disponible</p>
          <p className="text-tienda-muted text-sm mt-0.5 leading-snug">
            Hay una nueva versión lista. La app se recargará al aplicar.
          </p>
          <button
            onClick={handleAplicar}
            disabled={aplicando}
            className="mt-3 btn-primary px-5 py-2 text-sm disabled:opacity-60"
          >
            {aplicando ? 'Aplicando...' : 'Aplicar actualización'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Tab General ───────────────────────────────────────────────

function TabGeneral() {
  const { config, guardarConfig } = useConfig()
  const { estado: estadoBT, nombre: nombreBT, parear, desconectar } = useBTPrinter()
  const [nombre, setNombre] = useState(config.nombreTienda ?? '')
  const [guardando, setGuardando] = useState(false)
  const [exito, setExito] = useState(false)
  const [errorBT, setErrorBT] = useState(null)

  useEffect(() => { setNombre(config.nombreTienda ?? '') }, [config.nombreTienda])

  async function handleParear() {
    setErrorBT(null)
    try { await parear() } catch (e) { setErrorBT(e.message) }
  }

  async function handleGuardar(e) {
    e.preventDefault()
    if (!nombre.trim()) return
    setGuardando(true)
    await guardarConfig({ nombreTienda: nombre.trim() })
    setGuardando(false)
    setExito(true)
    setTimeout(() => setExito(false), 2500)
  }

  return (
    <div className="space-y-4">
      <p className="text-tienda-muted text-sm">
        Personaliza el nombre de tu tienda. Aparece en la barra superior y en los tickets de venta.
      </p>

      <form onSubmit={handleGuardar} className="space-y-4">
        <div>
          <label className="block text-tienda-muted text-sm mb-1">Nombre de la tienda</label>
          <input
            type="text"
            value={nombre}
            onChange={e => setNombre(e.target.value)}
            placeholder="Ej. Abarrotes La Esperanza"
            className="input-field"
          />
        </div>

        {nombre.trim() && (
          <div className="card p-4 flex items-center gap-3">
            <span className="text-2xl">🛒</span>
            <span className="font-bold text-tienda-text">{nombre.trim()}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={guardando || !nombre.trim()}
          className="btn-primary w-full py-3 disabled:opacity-50"
        >
          {guardando ? 'Guardando...' : 'Guardar nombre'}
        </button>
      </form>

      {exito && (
        <div className="bg-tienda-success/10 border border-tienda-success/30 rounded-xl p-3">
          <p className="text-tienda-success text-sm font-medium text-center">Nombre actualizado</p>
        </div>
      )}

      {/* Toggle fiado */}
      <div className="card p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-tienda-text font-semibold text-sm">Módulo de fiado</p>
            <p className="text-tienda-muted text-xs mt-0.5 leading-relaxed">
              Permite vender a crédito y llevar el control de deudas de clientes
            </p>
          </div>
          <button
            onClick={() => guardarConfig({ fiadoHabilitado: !config.fiadoHabilitado })}
            className={`relative flex-shrink-0 w-12 h-6 rounded-full overflow-hidden transition-colors duration-200
              ${config.fiadoHabilitado ? 'bg-tienda-success' : 'bg-tienda-border'}`}
            aria-label="Toggle módulo de fiado"
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-200
              ${config.fiadoHabilitado ? 'translate-x-[26px]' : 'translate-x-0.5'}`}
            />
          </button>
        </div>
      </div>

      {/* Toggle impresión automática */}
      <div className="card p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-tienda-text font-semibold text-sm">Impresión automática de tickets</p>
            <p className="text-tienda-muted text-xs mt-0.5 leading-relaxed">
              Imprime el ticket al terminar cada venta
            </p>
          </div>
          <button
            onClick={() => guardarConfig({ imprimirTicket: !config.imprimirTicket })}
            className={`relative flex-shrink-0 w-12 h-6 rounded-full overflow-hidden transition-colors duration-200
              ${config.imprimirTicket ? 'bg-tienda-success' : 'bg-tienda-border'}`}
            aria-label="Toggle impresión automática"
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-200
              ${config.imprimirTicket ? 'translate-x-[26px]' : 'translate-x-0.5'}`}
            />
          </button>
        </div>
      </div>

      {/* Impresora Bluetooth */}
      <div className="card p-4 space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-tienda-text font-semibold text-sm">Impresora Bluetooth</p>
            <p className="text-tienda-muted text-xs mt-0.5 leading-relaxed">
              {estadoBT === 'listo'
                ? nombreBT
                : estadoBT === 'conectando'
                ? 'Conectando…'
                : 'Sin impresora conectada'}
            </p>
          </div>
          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0
            ${estadoBT === 'listo'
              ? 'bg-tienda-success'
              : estadoBT === 'conectando'
              ? 'bg-yellow-400 animate-pulse'
              : 'bg-tienda-border'}`}
          />
        </div>

        {!navigator.bluetooth ? (
          <p className="text-tienda-danger text-xs">
            Bluetooth no disponible. Usa Chrome en Android para imprimir por BT.
          </p>
        ) : estadoBT === 'listo' ? (
          <button onClick={desconectar} className="btn-ghost w-full text-sm py-2.5">
            Desconectar impresora
          </button>
        ) : (
          <button
            onClick={handleParear}
            disabled={estadoBT === 'conectando'}
            className="btn-primary w-full text-sm py-2.5 disabled:opacity-50"
          >
            {estadoBT === 'conectando' ? 'Conectando…' : 'Conectar impresora BT'}
          </button>
        )}

        {errorBT && (
          <p className="text-tienda-danger text-xs">{errorBT}</p>
        )}
      </div>
    </div>
  )
}

// ── Tab Usuarios ──────────────────────────────────────────────

function TabUsuarios() {
  const { usuario: yo } = useAuth()
  const [usuarios, setUsuarios] = useState([])
  const [cargando, setCargando] = useState(true)
  const [modal, setModal] = useState(null) // null | 'nuevo' | { usuario }

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setCargando(true)
    setUsuarios(await obtenerUsuarios())
    setCargando(false)
  }

  return (
    <div className="space-y-3">
      <p className="text-tienda-muted text-sm">
        Administra los accesos a la aplicación. Solo el administrador puede gestionar usuarios.
      </p>

      {/* Lista */}
      {cargando ? (
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-4 border-tienda-accent border-t-transparent rounded-full animate-spin" />
        </div>
      ) : usuarios.map(u => (
        <UsuarioCard
          key={u._id}
          usuario={u}
          esSelf={u._id === yo?._id}
          onClick={() => setModal({ usuario: u })}
        />
      ))}

      {/* Botón nuevo */}
      <button
        onClick={() => setModal('nuevo')}
        className="w-full card p-4 flex items-center gap-3 text-tienda-accent active:scale-95 transition-transform border-dashed"
      >
        <div className="w-10 h-10 rounded-xl border-2 border-dashed border-tienda-accent/40 flex items-center justify-center text-xl font-bold">
          +
        </div>
        <span className="font-semibold text-sm">Nuevo usuario</span>
      </button>

      {/* Modal */}
      {modal === 'nuevo' && (
        <ModalUsuario
          onGuardar={async (nombre, pin) => {
            await crearUsuario(nombre, 'ventas', pin)
            await cargar()
            setModal(null)
          }}
          onCerrar={() => setModal(null)}
        />
      )}
      {modal && modal.usuario && (
        <ModalEditarUsuario
          usuario={modal.usuario}
          esSelf={modal.usuario._id === yo?._id}
          onGuardar={async (datos) => {
            await actualizarUsuario({ ...modal.usuario, ...datos })
            await cargar()
            setTimeout(() => setModal(null), 700)
          }}
          onCambiarPin={async (nuevoPin) => {
            await cambiarPin(modal.usuario._id, nuevoPin)
            setModal(null)
          }}
          onCerrar={() => setModal(null)}
        />
      )}
    </div>
  )
}

function UsuarioCard({ usuario, esSelf, onClick }) {
  const esAdmin = usuario.rol === 'admin'
  return (
    <button
      onClick={onClick}
      className="w-full card p-4 flex items-center gap-3 text-left active:scale-[0.98] transition-transform"
    >
      <div className={`w-11 h-11 rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0
        ${esAdmin ? 'bg-tienda-accent/20 text-tienda-accent' : 'bg-tienda-surface border border-tienda-border text-tienda-text'}`}>
        {usuario.nombre.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-tienda-text truncate">
          {usuario.nombre}
          {esSelf && <span className="text-tienda-muted text-xs ml-2">(tú)</span>}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full
            ${esAdmin ? 'bg-tienda-accent/15 text-tienda-accent' : 'bg-blue-500/15 text-blue-400'}`}>
            {esAdmin ? 'Admin' : 'Ventas'}
          </span>
          {!usuario.activo && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-tienda-danger/15 text-tienda-danger">
              Inactivo
            </span>
          )}
        </div>
      </div>
      <svg className="w-4 h-4 text-tienda-muted flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="m9 18 6-6-6-6" />
      </svg>
    </button>
  )
}

const TECLAS_PIN = ['1','2','3','4','5','6','7','8','9','','0','⌫']

function PinPad({ value, onChange, placeholder = 'PIN (4 dígitos)' }) {
  return (
    <div className="space-y-3">
      {/* Indicadores */}
      <div className="flex justify-center gap-3">
        {[0,1,2,3].map(i => (
          <div key={i} className={`w-4 h-4 rounded-full border-2 transition-colors
            ${i < value.length ? 'bg-tienda-accent border-tienda-accent' : 'border-tienda-border'}`} />
        ))}
      </div>
      <p className="text-center text-tienda-muted text-xs">{placeholder}</p>
      {/* Teclado */}
      <div className="grid grid-cols-3 gap-2">
        {TECLAS_PIN.map((t, i) => (
          <button
            key={i}
            type="button"
            disabled={!t}
            onClick={() => {
              if (t === '⌫') { onChange(value.slice(0, -1)); return }
              if (value.length < 4) onChange(value + t)
            }}
            className={`h-12 rounded-2xl text-xl font-bold flex items-center justify-center
              active:scale-95 transition-transform select-none
              ${!t ? 'invisible' : t === '⌫'
                ? 'bg-tienda-danger/20 text-tienda-danger border border-tienda-danger/30'
                : 'bg-tienda-bg border border-tienda-border text-tienda-text active:bg-tienda-border'}`}
          >
            {t}
          </button>
        ))}
      </div>
    </div>
  )
}

function ModalUsuario({ onGuardar, onCerrar }) {
  const [paso, setPaso] = useState('datos') // datos | pin | confirmar
  const [nombre, setNombre] = useState('')
  const [pin, setPin] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [guardando, setGuardando] = useState(false)

  function handleSiguiente() {
    if (!nombre.trim()) { setError('El nombre es obligatorio'); return }
    setError('')
    setPaso('pin')
  }

  function handlePin(v) {
    setPin(v)
    if (v.length === 4) { setError(''); setPaso('confirmar'); setConfirm('') }
  }

  async function handleConfirmar(v) {
    setConfirm(v)
    if (v.length < 4) return
    if (v !== pin) { setError('Los PIN no coinciden'); setPin(''); setConfirm(''); setPaso('pin'); return }
    setGuardando(true)
    await onGuardar(nombre.trim(), pin)
    setGuardando(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-tienda-surface w-full max-w-lg rounded-t-3xl overflow-hidden flex flex-col max-h-[85vh]">

        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-tienda-border flex-shrink-0">
          <div className="flex items-center gap-3">
            {paso !== 'datos' && (
              <button onClick={() => { setPaso(paso === 'confirmar' ? 'pin' : 'datos'); setPin(''); setConfirm(''); setError('') }}
                className="text-tienda-muted">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M19 12H5M5 12l7-7M5 12l7 7" />
                </svg>
              </button>
            )}
            <h2 className="text-xl font-bold text-tienda-text">Nuevo usuario</h2>
          </div>
          <button onClick={onCerrar} className="w-9 h-9 flex items-center justify-center text-tienda-muted">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {error && (
            <div className="bg-tienda-danger/10 border border-tienda-danger/30 rounded-xl p-3">
              <p className="text-tienda-danger text-sm font-medium">{error}</p>
            </div>
          )}

          {paso === 'datos' && (
            <>
              <div>
                <label className="block text-tienda-muted text-sm mb-1">Nombre</label>
                <input
                  autoFocus
                  className="input-field"
                  placeholder="Ej. María López"
                  value={nombre}
                  onChange={e => setNombre(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSiguiente()}
                />
              </div>
              <div className="bg-tienda-bg rounded-xl border border-tienda-border p-3 flex items-center gap-2">
                <span className="text-blue-400 font-semibold text-xs px-2 py-0.5 rounded-full bg-blue-500/15">Ventas</span>
                <span className="text-tienda-muted text-xs">Acceso a cobro y clientes</span>
              </div>
            </>
          )}

          {paso === 'pin' && (
            <PinPad value={pin} onChange={handlePin} placeholder="Define un PIN de 4 dígitos" />
          )}

          {paso === 'confirmar' && (
            <PinPad value={confirm} onChange={handleConfirmar} placeholder="Confirma el PIN" />
          )}
        </div>

        {paso === 'datos' && (
          <div className="p-5 border-t border-tienda-border flex-shrink-0">
            <button
              onClick={handleSiguiente}
              disabled={!nombre.trim()}
              className="btn-primary w-full text-center disabled:opacity-40"
            >
              Siguiente
            </button>
          </div>
        )}

        {guardando && (
          <div className="p-5 border-t border-tienda-border flex-shrink-0 text-center text-tienda-muted text-sm animate-pulse">
            Guardando…
          </div>
        )}
      </div>
    </div>
  )
}

function ModalEditarUsuario({ usuario, esSelf, onGuardar, onCambiarPin, onCerrar }) {
  const esAdmin = usuario.rol === 'admin'
  const [nombre, setNombre] = useState(usuario.nombre)
  const [activo, setActivo] = useState(usuario.activo)
  const [cambiandoPin, setCambiandoPin] = useState(false)
  const [pin, setPin] = useState('')
  const [confirm, setConfirm] = useState('')
  const [pasoPin, setPasoPin] = useState('nuevo') // nuevo | confirmar
  const [error, setError] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [guardadoExito, setGuardadoExito] = useState(false)

  async function handleGuardar() {
    if (!nombre.trim()) { setError('El nombre es obligatorio'); return }
    setGuardando(true)
    await onGuardar({ nombre: nombre.trim(), activo })
    setGuardadoExito(true)
    setGuardando(false)
  }

  function handlePin(v) {
    setPin(v)
    if (v.length === 4) { setError(''); setPasoPin('confirmar'); setConfirm('') }
  }

  async function handleConfirmarPin(v) {
    setConfirm(v)
    if (v.length < 4) return
    if (v !== pin) { setError('Los PIN no coinciden'); setPin(''); setConfirm(''); setPasoPin('nuevo'); return }
    setGuardando(true)
    await onCambiarPin(pin)
    setGuardando(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-tienda-surface w-full max-w-lg rounded-t-3xl overflow-hidden flex flex-col max-h-[85vh]">

        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-tienda-border flex-shrink-0">
          <div className="flex items-center gap-3">
            {cambiandoPin && (
              <button onClick={() => { setCambiandoPin(false); setPin(''); setConfirm(''); setPasoPin('nuevo'); setError('') }}
                className="text-tienda-muted">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M19 12H5M5 12l7-7M5 12l7 7" />
                </svg>
              </button>
            )}
            <div>
              <h2 className="text-xl font-bold text-tienda-text">
                {cambiandoPin ? 'Cambiar PIN' : 'Editar usuario'}
              </h2>
              {!cambiandoPin && (
                <p className="text-tienda-muted text-sm">{usuario.nombre}</p>
              )}
            </div>
          </div>
          <button onClick={onCerrar} className="w-9 h-9 flex items-center justify-center text-tienda-muted">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {error && (
            <div className="bg-tienda-danger/10 border border-tienda-danger/30 rounded-xl p-3">
              <p className="text-tienda-danger text-sm font-medium">{error}</p>
            </div>
          )}

          {!cambiandoPin && (
            <>
              <div>
                <label className="block text-tienda-muted text-sm mb-1">Nombre</label>
                <input
                  autoFocus
                  className="input-field"
                  value={nombre}
                  onChange={e => setNombre(e.target.value)}
                />
              </div>

              <div className="bg-tienda-bg rounded-xl border border-tienda-border p-3 flex items-center gap-2">
                <span className={`font-semibold text-xs px-2 py-0.5 rounded-full
                  ${esAdmin ? 'bg-tienda-accent/15 text-tienda-accent' : 'bg-blue-500/15 text-blue-400'}`}>
                  {esAdmin ? 'Admin' : 'Ventas'}
                </span>
                <span className="text-tienda-muted text-xs">
                  {esAdmin ? 'Acceso completo' : 'Acceso a cobro y clientes'}
                </span>
              </div>

              {!esAdmin && (
                <div className="flex items-center justify-between p-4 card">
                  <div>
                    <p className="font-semibold text-tienda-text text-sm">Usuario activo</p>
                    <p className="text-tienda-muted text-xs">
                      {activo ? 'Puede iniciar sesión' : 'Sin acceso a la app'}
                    </p>
                  </div>
                  <button
                    onClick={() => setActivo(v => !v)}
                    className={`w-12 h-6 rounded-full overflow-hidden transition-colors flex-shrink-0 relative
                      ${activo ? 'bg-tienda-success' : 'bg-tienda-border'}`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all
                      ${activo ? 'translate-x-[26px]' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              )}

              <button
                onClick={() => { setCambiandoPin(true); setPin(''); setConfirm(''); setPasoPin('nuevo') }}
                className="w-full card p-4 flex items-center gap-3 text-tienda-muted active:scale-95 transition-transform"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <span className="text-sm font-semibold">Cambiar PIN</span>
              </button>
            </>
          )}

          {cambiandoPin && pasoPin === 'nuevo' && (
            <PinPad value={pin} onChange={handlePin} placeholder="Nuevo PIN de 4 dígitos" />
          )}

          {cambiandoPin && pasoPin === 'confirmar' && (
            <PinPad value={confirm} onChange={handleConfirmarPin} placeholder="Confirma el nuevo PIN" />
          )}
        </div>

        {!cambiandoPin && (
          <div className="p-5 border-t border-tienda-border flex-shrink-0 flex gap-3">
            <button onClick={onCerrar} className="btn-ghost flex-1 text-center">
              Cancelar
            </button>
            <button
              onClick={handleGuardar}
              disabled={!nombre.trim() || guardando || guardadoExito}
              className="btn-primary flex-1 text-center disabled:opacity-40"
            >
              {guardando ? 'Guardando…' : guardadoExito ? '✓ Guardado' : 'Guardar'}
            </button>
          </div>
        )}

        {guardando && cambiandoPin && (
          <div className="p-5 border-t border-tienda-border flex-shrink-0 text-center text-tienda-muted text-sm animate-pulse">
            Guardando…
          </div>
        )}
      </div>
    </div>
  )
}

// ── Tab Respaldo ──────────────────────────────────────────────

function TabRespaldo() {
  const inputRef = useRef(null)
  const [estado, setEstado] = useState('idle')
  const [mensaje, setMensaje] = useState('')
  const [passphrase, setPassphrase] = useState('')

  function handleClickImportar() { setEstado('confirmando') }
  function handleCancelarConfirm() { setEstado('idle') }
  function handleConfirmarImport() { setEstado('idle'); inputRef.current?.click() }

  async function handleExportar() {
    if (passphrase.length < 8) {
      setMensaje('Usa una contrasena de al menos 8 caracteres')
      setEstado('error')
      return
    }
    setEstado('cargando')
    try {
      await exportarDatos(passphrase)
      setEstado('idle')
    } catch (e) {
      setMensaje('Error al exportar: ' + e.message)
      setEstado('error')
    }
  }

  async function handleImportar(e) {
    const archivo = e.target.files?.[0]
    if (!archivo) return
    setEstado('cargando')
    setMensaje('')
    try {
      const r = await importarDatos(archivo, passphrase)
      setMensaje(`Restaurado: ${r.productos} productos · ${r.ventas} ventas · ${r.clientes} clientes · ${r.movimientos} movimientos`)
      setEstado('exito')
    } catch (err) {
      setMensaje(err.message || 'Archivo inválido')
      setEstado('error')
    } finally {
      e.target.value = ''
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-tienda-muted text-sm">
        Exporta todos los datos de la tienda en un archivo cifrado para copias de seguridad o transferencias.
      </p>

      <div>
        <label htmlFor="backup-passphrase" className="block text-tienda-muted text-sm mb-1">Contrasena del respaldo</label>
        <input
          id="backup-passphrase"
          type="password"
          className="input-field"
          value={passphrase}
          minLength={8}
          autoComplete="new-password"
          onChange={e => setPassphrase(e.target.value)}
          placeholder="Minimo 8 caracteres"
        />
        <p className="text-tienda-muted text-xs mt-1">Los respaldos nuevos se cifran antes de descargarse.</p>
      </div>

      <div className="space-y-3">
        <button
          onClick={handleExportar}
          disabled={estado === 'cargando'}
          className="w-full card p-4 flex items-center gap-4 active:scale-95 transition-transform disabled:opacity-50"
        >
          <div className="w-12 h-12 rounded-2xl bg-tienda-accent/10 flex items-center justify-center text-tienda-accent flex-shrink-0">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </div>
          <div className="text-left">
            <p className="font-semibold text-tienda-text">Exportar respaldo</p>
            <p className="text-tienda-muted text-xs">Descarga un archivo .practicaja cifrado con todos los datos</p>
          </div>
        </button>

        <button
          onClick={handleClickImportar}
          disabled={estado === 'cargando'}
          className="w-full card p-4 flex items-center gap-4 active:scale-95 transition-transform disabled:opacity-50"
        >
          <div className="w-12 h-12 rounded-2xl bg-tienda-accent/10 flex items-center justify-center text-tienda-accent flex-shrink-0">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>
          <div className="text-left">
            <p className="font-semibold text-tienda-text">Importar respaldo</p>
            <p className="text-tienda-muted text-xs">Restaura archivos .practicaja o respaldos .json anteriores</p>
          </div>
        </button>
        <input ref={inputRef} type="file" accept=".practicaja,.json" className="hidden" onChange={handleImportar} />
      </div>

      {estado === 'confirmando' && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-tienda-surface rounded-3xl w-full max-w-sm p-6 space-y-4">
            <div className="text-center">
              <p className="text-4xl mb-3">⚠️</p>
              <h3 className="text-xl font-bold text-tienda-text">Importar respaldo</h3>
              <p className="text-tienda-muted text-sm mt-2">
                Esta acción <span className="text-tienda-danger font-semibold">reemplazará todos los datos actuales</span> con los del archivo seleccionado.
              </p>
              <ul className="text-tienda-muted text-sm mt-3 space-y-1 text-left">
                <li className="flex items-start gap-2">
                  <span className="text-tienda-danger font-bold mt-0.5">!</span>
                  <span>Productos, ventas, clientes y movimientos actuales se <strong className="text-tienda-text">borrarán</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-tienda-success font-bold mt-0.5">✓</span>
                  <span>Se restaurarán los datos del <strong className="text-tienda-text">archivo seleccionado</strong></span>
                </li>
              </ul>
            </div>
            <div className="flex gap-3">
              <button onClick={handleCancelarConfirm} className="btn-ghost flex-1 text-center text-base">Cancelar</button>
              <button onClick={handleConfirmarImport} className="btn-danger flex-1 text-center text-base">Continuar</button>
            </div>
          </div>
        </div>
      )}

      {estado === 'cargando' && (
        <p className="text-center text-tienda-muted text-sm animate-pulse">Procesando…</p>
      )}
      {estado === 'exito' && (
        <div className="bg-tienda-success/10 border border-tienda-success/30 rounded-xl p-3">
          <p className="text-tienda-success text-sm font-medium">{mensaje}</p>
        </div>
      )}
      {estado === 'error' && (
        <div className="bg-tienda-danger/10 border border-tienda-danger/30 rounded-xl p-3">
          <p className="text-tienda-danger text-sm font-medium">{mensaje}</p>
        </div>
      )}
    </div>
  )
}

// ── Tab Sync ───────────────────────────────────────────────────

function TabSync() {
  const { licencia, estado: estadoLic, diasRestantes, activar } = useLicencia()
  const { estado: estadoSync, ultimaSync } = useSync()

  const [codigo, setCodigo] = useState('')
  const [activando, setActivando] = useState(false)
  const [errorActivar, setErrorActivar] = useState(null)
  const [exitoActivar, setExitoActivar] = useState(false)

  const tieneLicencia = estadoLic === 'activo'
  const tienePlanSync = licencia?.plan === 'sync'

  async function handleActivar(e) {
    e.preventDefault()
    if (!codigo.trim()) return
    setActivando(true)
    setErrorActivar(null)
    setExitoActivar(false)
    try {
      await activar(codigo.trim())
      setCodigo('')
      setExitoActivar(true)
    } catch (err) {
      setErrorActivar(err.message)
    } finally {
      setActivando(false)
    }
  }

  const coloresSyncEstado = {
    conectando: 'text-yellow-400',
    activo:     'text-tienda-success',
    pausado:    'text-tienda-muted',
    error:      'text-tienda-danger',
    inactivo:   'text-tienda-muted',
  }

  const labelsSyncEstado = {
    conectando: '⏳ Conectando…',
    activo:     '✅ Activo',
    pausado:    '⏸ En pausa',
    error:      '❌ Error de conexión',
    inactivo:   '— Inactivo',
  }

  return (
    <div className="space-y-5">

      {/* Estado de licencia */}
      <div className="card p-4 space-y-3">
        <p className="text-tienda-muted text-xs font-semibold uppercase tracking-wide">Estado de licencia</p>

        {estadoLic === 'trial' && (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-tienda-text font-semibold text-sm">Periodo de prueba</p>
              <p className="text-tienda-muted text-xs">{diasRestantes} días restantes</p>
            </div>
            <span className="badge-success text-xs px-2 py-1 rounded-lg bg-tienda-success/10 text-tienda-success font-bold">
              Trial
            </span>
          </div>
        )}

        {tieneLicencia && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-tienda-text font-semibold text-sm">{licencia.nombre}</p>
                <p className="text-tienda-muted text-xs">ID: {licencia.tiendaId?.slice(0, 8)}…</p>
              </div>
              <span className={`text-xs px-2 py-1 rounded-lg font-bold
                ${tienePlanSync
                  ? 'bg-blue-500/10 text-blue-400'
                  : 'bg-tienda-accent/10 text-tienda-accent'}`}>
                {tienePlanSync ? 'Sync' : 'Básico'}
              </span>
            </div>
            <div className="text-tienda-muted text-xs space-y-0.5">
              <p>Activado: {licencia.emitido}</p>
              {licencia.expira && <p>Vence: {licencia.expira}</p>}
            </div>
          </div>
        )}
      </div>

      {/* Estado de sincronización (solo si tiene plan sync) */}
      {tienePlanSync && (
        <div className="card p-4 space-y-3">
          <p className="text-tienda-muted text-xs font-semibold uppercase tracking-wide">Sincronización</p>
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-semibold text-sm ${coloresSyncEstado[estadoSync] ?? 'text-tienda-muted'}`}>
                {labelsSyncEstado[estadoSync] ?? estadoSync}
              </p>
              {ultimaSync && (
                <p className="text-tienda-muted text-xs">
                  Última sync: {ultimaSync.toLocaleTimeString()}
                </p>
              )}
            </div>
          </div>
          <div className="text-tienda-muted text-xs space-y-0.5 border-t border-tienda-border pt-2">
            <p className="font-semibold text-tienda-text text-xs">Servidor</p>
            <p className="font-mono break-all">
              {(() => { try { return new URL(licencia.syncUrl).hostname } catch { return '••••••••' } })()}
            </p>
            <p>Usuario: {licencia.syncUser?.slice(0, 3)}{'•'.repeat(Math.max(0, (licencia.syncUser?.length ?? 0) - 3))}</p>
          </div>
        </div>
      )}

      {/* Activar nuevo código */}
      <div className="card p-4 space-y-3">
        <p className="text-tienda-muted text-xs font-semibold uppercase tracking-wide">
          {tieneLicencia ? 'Actualizar licencia' : 'Activar licencia'}
        </p>
        <p className="text-tienda-muted text-xs">
          {tieneLicencia
            ? 'Puedes ingresar un nuevo código para renovar o cambiar de plan.'
            : 'Ingresa el código que recibiste al adquirir tu licencia.'}
        </p>

        <form onSubmit={handleActivar} className="space-y-3">
          <textarea
            value={codigo}
            onChange={e => { setCodigo(e.target.value); setErrorActivar(null); setExitoActivar(false) }}
            placeholder="Pega aquí tu código de activación..."
            rows={3}
            className="input-field w-full text-xs font-mono resize-none"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
          />

          {errorActivar && (
            <p className="text-tienda-danger text-xs font-semibold">❌ {errorActivar}</p>
          )}
          {exitoActivar && (
            <p className="text-tienda-success text-xs font-semibold">✅ Licencia activada correctamente</p>
          )}

          <button
            type="submit"
            disabled={!codigo.trim() || activando}
            className="btn-primary w-full disabled:opacity-40"
          >
            {activando ? 'Verificando…' : 'Activar código'}
          </button>
        </form>
      </div>

    </div>
  )
}
